import {
  Action,
  createFeatureSelector,
  createReducer,
  createSelector,
  on
} from '@ngrx/store';

import { SchuecoProductValidationResponseInterface } from '@p1/libs/schueco-product-validation/src/lib/model/schueco-product-validation-response.interface';
import { GlazingInterface } from '@p1/libs/schueco-product-validation/src/lib/model/glazing.interface';
import { OpeningTypeInterface } from '@p1/libs/schueco-product-validation/src/lib/model/opening-type.interface';

import * as productValidationActions from '../actions/product-validation.actions';
import { deepCopy } from '../../shared/common/helper/deep-copy/deep-copy';
import { translateValueLabels } from '../helper/product-validation-helper';


export const productValidationFeatureKey = 'productValidation';

export interface State {
  productValidationServiceIsAlive: boolean;
  fetchProductValidationInProgress: boolean;
  productValidationInProgress: boolean;
  isValidationPossibleForActiveProduct: boolean;
  activeValidationUuid?: string;
  productValidationResult?: SchuecoProductValidationResponseInterface;
  inputValues?: {
    openingTypes?: OpeningTypeInterface[];
    glazing?: GlazingInterface[];
  };
}

export const initialState: State = {
  productValidationServiceIsAlive: true,
  fetchProductValidationInProgress: false,
  productValidationInProgress: false,
  isValidationPossibleForActiveProduct: false
};

const productValidationReducers = createReducer(
  initialState,
  on(productValidationActions.serviceStatusRequested,
    (state, { alive }) => ({
      ...state,
      productValidationServiceIsAlive: alive
    })),
  on(productValidationActions.checkIfValidationIsPossibleSuccess,
    (state, { isPossible }) => ({
      ...state,
      isValidationPossibleForActiveProduct: isPossible
    })),
  on(productValidationActions.fetchProductValidationTriggered,
    (state) => ({
      ...state,
      fetchProductValidationInProgress: true
    })),
  on(productValidationActions.fetchProductValidationTriggeredSuccess,
    (state, { validationUuid, validationResult }) => ({
      ...state,
      fetchProductValidationInProgress: false,
      productValidationResult: {
        ...validationResult,
        validationUuid
      }
    })),
  on(productValidationActions.fetchProductValidationTriggeredFailure,
    (state) => ({
      ...state,
      fetchProductValidationInProgress: false
    })),
  on(productValidationActions.productValidationTriggered,
    (state) => ({
      ...state,
      productValidationInProgress: true,
      productValidationResult: null,
      activeValidationUuid: null
    })),
  on(productValidationActions.productValidationTriggeredSuccess,
    (state, { validationUuid }) => ({
      ...state,
      productValidationInProgress: false,
      activeValidationUuid: validationUuid
    })),
  on(productValidationActions.productValidationTriggeredFailure,
    (state) => ({
      ...state,
      productValidationInProgress: false
    })),
  on(productValidationActions.configOptionsRequestedSuccess,
    (state, { configOptions }) => {
      const tmpState: State = deepCopy(state);

      return {
        ...tmpState,
        inputValues: {
          ...tmpState.inputValues,
          glazing: configOptions.supportedGlassconfigs,
          openingTypes: configOptions.supportedOpeningtypes
        }
      };
    }
  )
);

export const reducers = (state: State | undefined, action: Action) => productValidationReducers(state, action);

export const getProductValidationState = createFeatureSelector<State>(productValidationFeatureKey);

export const getProductValidationServiceIsAlive = createSelector(getProductValidationState, state => state ? state.productValidationServiceIsAlive : null);
export const getFetchProductValidationInProgress = createSelector(getProductValidationState, state => state ? state.fetchProductValidationInProgress : null);
export const getIsValidationPossible = createSelector(getProductValidationState, state => state ? state.isValidationPossibleForActiveProduct : null);
export const getProductValidationResult = createSelector(getProductValidationState, state => state ? state.productValidationResult : null);
export const getInputValues = createSelector(getProductValidationState, state => state ? state.inputValues : null);
export const getOpeningTypes = createSelector(getInputValues, inputValues => inputValues?.openingTypes.map(openingType => translateValueLabels('openingType', openingType.label, openingType.value)) ?? null);
export const getGlazing = createSelector(getInputValues, inputValues => inputValues?.glazing.map(glazing => translateValueLabels('glazing', glazing.label, glazing.value)) ?? null);
